/**
 * Projeção de quantidades da etiqueta a partir do ledger `baixas[]` — épico
 * #323, fase F1 (issue #324).
 *
 * Regra que resume tudo: **o ledger é a fonte da verdade e os cinco caches são
 * projeção dele**. `consumedQuantity`, `discardedQuantity` e `expiredQuantity`
 * são somas por `kind` do ledger EFETIVO (desfechos sem estorno válido);
 * `remainingQuantity` e `overrunQuantity` saem dessas somas contra a
 * `quantity` original. Nunca cache a partir de cache.
 *
 * Compartilhado por Functions e web; espelhado à mão em Kotlin (a paridade é
 * garantida por teste dos dois lados, não por geração).
 */

import {
  LabelStatus,
  QUANTITY_MAX_DECIMALS,
  SYSTEM_ACTOR_UID,
  StatusChangedVia,
  type UnitOfMeasure,
  unitAllowsDecimal,
} from './enums';
import {
  LABEL_BAIXAS_MAX,
  type LabelBaixa,
  type LabelBaixaMovement,
  type LabelBaixaUndo,
  type LabelOutcomeKind,
} from './labelFieldContract';

/**
 * Tolerância para comparação de quantidades. Existe porque `0.1 + 0.2` não é
 * `0.3` e o produto aceita 3 casas (`QUANTITY_MAX_DECIMALS`) — comparar por
 * igualdade exata faria uma etiqueta zerada parecer ter 4e-16 kg vigentes e
 * nunca fechar.
 */
export const QUANTITY_EPSILON = 1e-6;

/** Arredonda a 3 casas (a precisão máxima que o produto aceita, issue #207). */
export function round3(value: number): number {
  return Math.round(value * 1000) / 1000;
}

/**
 * Teto da quantidade de uma etiqueta. Não é regra de negócio fina — é o
 * guarda-corpo contra dedo pesado ("2000000 kg de frango") e contra número que
 * estoura a formatação.
 */
export const QUANTITY_MAX = 100000;

/**
 * Valida uma quantidade de etiqueta conforme a unidade do produto (issue #39):
 * positiva, dentro de {@link QUANTITY_MAX}, e com casas decimais só onde a
 * unidade aceita (kg/L, até {@link QUANTITY_MAX_DECIMALS} casas). `GRAM`,
 * `MILLILITER`, `UNIT`, `PACK` e `BOX` exigem inteiro — "1,5 unidades" não
 * existe; volume fracionário fino se modela escolhendo g/ml.
 *
 * `unit` ausente (nenhum produto resolvido) trata como inteiro: é o mais
 * restritivo, e nesse estado o formulário está bloqueado por outro motivo.
 *
 * Vive aqui, e não em cada tela, porque o **campo de criação** e o **campo de
 * baixa** precisam concordar: divergência entre os dois deixaria o operador
 * gravar um valor que a outra ponta recusa.
 */
export function isQuantityValidForUnit(
  quantity: number,
  unit: UnitOfMeasure | null | undefined,
): boolean {
  if (!Number.isFinite(quantity) || quantity <= 0 || quantity > QUANTITY_MAX) return false;
  if (unit === null || unit === undefined || !unitAllowsDecimal(unit)) {
    return Number.isInteger(quantity);
  }
  // `toFixed` arredonda; se o valor não muda, é porque já tinha ≤ N casas.
  return Number(quantity.toFixed(QUANTITY_MAX_DECIMALS)) === quantity;
}

/**
 * Lê o que o operador digitou num campo de quantidade. Aceita **vírgula** como
 * separador decimal: em pt-BR/es/it/fr o teclado numérico entrega vírgula, e
 * recusar "1,5" seria recusar a forma normal de escrever o número.
 *
 * Devolve `null` para texto vazio ou que não é número — quem chama decide se
 * isso é "campo em branco" ou "valor inválido".
 */
export function parseQuantityInput(text: string): number | null {
  const normalized = text.trim().replace(',', '.');
  if (normalized.length === 0) return null;
  const value = Number(normalized);
  return Number.isFinite(value) ? value : null;
}

/** `true` quando o valor é praticamente zero, dentro da tolerância. */
export function isEffectivelyZero(value: number): boolean {
  return Math.abs(value) < QUANTITY_EPSILON;
}

/** Doc de etiqueta lido de qualquer ponta: parseado pelo Zod ou cru do Firestore. */
export type LabelQuantitySource = {
  quantity?: number | null;
  status?: string | null;
  labelSchemaVersion?: number | null;
  consumedQuantity?: number | null;
  discardedQuantity?: number | null;
  expiredQuantity?: number | null;
  remainingQuantity?: number | null;
  overrunQuantity?: number | null;
  baixas?: readonly LabelBaixa[] | null;
  statusChangedAtMillis?: number | null;
  statusChangedByUid?: string | null;
  statusChangedByName?: string | null;
  statusChangedVia?: string | null;
  printedAtMillis?: number | null;
  printedByUid?: string | null;
  createdAtMillis?: number | null;
};

export type EffectiveLedger = {
  /** Desfechos que continuam de pé (sem estorno válido), em ordem cronológica. */
  readonly outcomes: readonly LabelBaixaMovement[];
  /** Desfechos estornados — ficam no histórico, sem efeito no saldo. */
  readonly reversed: readonly LabelBaixaMovement[];
  /**
   * Estornos ignorados: referência inexistente, entrada já estornada,
   * apontando para outro `UNDO`, ou com `reversesKind`/`quantity` que não
   * batem com o alvo (#795). Ficam no ledger como registro; o trigger loga
   * `label_undo_invalid`. Nunca são apagados — o ledger é imutável.
   */
  readonly invalidUndos: readonly LabelBaixaUndo[];
  /**
   * Total de entradas no array, estornos VÁLIDOS inclusos. É o número que conta
   * contra `LABEL_BAIXAS_MAX` — somar `outcomes + reversed + invalidUndos`
   * deixaria os estornos válidos de fora e o teto seria checado a menos.
   */
  readonly size: number;
};

function num(value: number | null | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function isOutcome(entry: LabelBaixa): entry is LabelBaixaMovement {
  return entry.kind !== 'UNDO';
}

/**
 * O array de entradas, tolerante a doc sem o campo. O cast existe porque
 * `Array.isArray` alarga um `readonly T[]` para `any[]` no narrowing do TS.
 */
function ledgerEntries(
  baixas: readonly LabelBaixa[] | null | undefined,
): readonly LabelBaixa[] {
  return Array.isArray(baixas) ? (baixas as readonly LabelBaixa[]) : [];
}

/**
 * Separa o ledger em efetivo × estornado × estorno inválido.
 *
 * Os estornos são processados em ordem cronológica (`atMillis`, desempate pela
 * posição no array) porque a regra é "uma entrada é estornada no MÁXIMO uma
 * vez": com dois gerentes desfazendo a mesma entrada offline, vale o primeiro
 * e o segundo é ignorado. As rules não conseguem varrer o array para checar
 * isso — quem garante é esta função, do lado do servidor.
 *
 * Também invalida o estorno cuja cópia (`reversesKind`, `quantity`) diverge
 * do alvo (#795, 2026-10-07): as rules confiam no `reversesKind` declarado.
 *
 * Usado por: Functions (`onLabelWritten`/`labelLedger.ts` — a autoridade),
 * web `apps/app` (`labelBaixaPatch.ts`, projeção otimista). Espelhos à mão:
 * Android `LabelQuantity.kt` e iOS `LabelQuantity.swift` (cache; o servidor
 * vence — a checagem da cópia foi espelhada nos dois em `d024a67f1`, #795).
 */
export function effectiveLedger(baixas: readonly LabelBaixa[] | null | undefined): EffectiveLedger {
  const entries = ledgerEntries(baixas);
  const outcomeById = new Map<string, LabelBaixaMovement>();
  for (const entry of entries) {
    if (isOutcome(entry) && !outcomeById.has(entry.id)) outcomeById.set(entry.id, entry);
  }

  const undos = entries
    .map((entry, index) => ({ entry, index }))
    .filter((it): it is { entry: LabelBaixaUndo; index: number } => it.entry.kind === 'UNDO')
    .sort((a, b) => a.entry.atMillis - b.entry.atMillis || a.index - b.index);

  const reversedIds = new Set<string>();
  const invalidUndos: LabelBaixaUndo[] = [];
  for (const { entry } of undos) {
    const target = outcomeById.get(entry.reverses);
    // Bug corrigido em 2026-10-07 13:15 +0200 (#795, auditoria 2º nível,
    // "`reversesKind` forjável"): `reversesKind` e `quantity` do estorno são
    // cópias que o CLIENTE preenche, e as rules escolhem a checagem pelo
    // valor declarado (inclusive a de "vencido não volta", só quando
    // `reversesKind == EXPIRED`). Um gerente estornava a entrada EXPIRED
    // declarando `CONSUMED`, a checagem de validade não rodava e o saldo
    // vencido voltava a PRINTED (fura a I7). As rules não leem o array, então
    // quem confere a cópia contra o alvo é esta função. Todo escritor
    // legítimo (web/Android/iOS `estorno`, `systemUndo`) copia os dois do
    // alvo; divergência é forja. Não afrouxar para "só o `reverses`".
    if (
      target === undefined ||
      reversedIds.has(entry.reverses) ||
      entry.reversesKind !== target.kind ||
      round3(entry.quantity) !== round3(target.quantity)
    ) {
      invalidUndos.push(entry);
      continue;
    }
    reversedIds.add(entry.reverses);
  }

  const outcomes: LabelBaixaMovement[] = [];
  const reversed: LabelBaixaMovement[] = [];
  for (const entry of entries) {
    if (!isOutcome(entry)) continue;
    (reversedIds.has(entry.id) ? reversed : outcomes).push(entry);
  }
  outcomes.sort((a, b) => a.atMillis - b.atMillis);

  return { outcomes, reversed, invalidUndos, size: entries.length };
}

/** Σ das quantidades dos desfechos efetivos de um `kind`. */
export function sumBaixas(ledger: EffectiveLedger, kind: LabelOutcomeKind): number {
  let total = 0;
  for (const entry of ledger.outcomes) {
    if (entry.kind === kind) total += entry.quantity;
  }
  return round3(total);
}

export type LabelProjection = {
  readonly consumedQuantity: number;
  readonly discardedQuantity: number;
  readonly expiredQuantity: number;
  readonly remainingQuantity: number;
  readonly overrunQuantity: number;
};

/**
 * Os cinco caches de uma vez, **direto do ledger efetivo**. É a única forma
 * suportada de derivá-los: calcular `remainingQuantity` a partir de
 * `consumedQuantity` já gravado seria cache de cache, e um drift no primeiro
 * se propagaria sem ninguém ver.
 *
 * `remainingQuantity` é clampado em zero e o que passou disso vira
 * `overrunQuantity` — o excesso do race offline (§5.1) é **sinalizado**, nunca
 * escondido, e o estoque nunca fica negativo.
 */
export function computeLabelProjection(
  quantity: number,
  ledger: EffectiveLedger,
): LabelProjection {
  const consumedQuantity = sumBaixas(ledger, 'CONSUMED');
  const discardedQuantity = sumBaixas(ledger, 'DISCARDED');
  const expiredQuantity = sumBaixas(ledger, 'EXPIRED');
  const moved = consumedQuantity + discardedQuantity + expiredQuantity;
  const original = Math.max(0, num(quantity));
  return {
    consumedQuantity,
    discardedQuantity,
    expiredQuantity,
    remainingQuantity: round3(Math.max(0, original - moved)),
    overrunQuantity: round3(Math.max(0, moved - original)),
  };
}

/**
 * O status de uma etiqueta cujo vigente chegou a zero, em função dos desfechos
 * presentes — **não** do último movimento. Um só desfecho → ele; dois ou mais,
 * em qualquer combinação → `MIXED` (5 kg consumidos + 15 kg vencidos é `MIXED`
 * válida). Devolve `null` quando não há desfecho efetivo nenhum.
 */
export function closedStatusFor(ledger: EffectiveLedger): LabelStatus | null {
  const present: LabelStatus[] = [];
  if (sumBaixas(ledger, 'CONSUMED') > QUANTITY_EPSILON) present.push(LabelStatus.CONSUMED);
  if (sumBaixas(ledger, 'DISCARDED') > QUANTITY_EPSILON) present.push(LabelStatus.DISCARDED);
  if (sumBaixas(ledger, 'EXPIRED') > QUANTITY_EPSILON) present.push(LabelStatus.EXPIRED);
  const [first] = present;
  if (first === undefined) return null;
  return present.length === 1 ? first : LabelStatus.MIXED;
}

/** `true` quando o doc já está no schema v2 (ledger materializado). */
export function isLabelSchemaV2(label: LabelQuantitySource): boolean {
  return num(label.labelSchemaVersion) >= 2;
}

/**
 * Ledger sintético de uma etiqueta v1 (§2.5): a baixa antiga era sempre total,
 * então o desfecho é único e vale a `quantity` inteira.
 *
 * Serve a dois consumidores que precisam produzir **a mesma** entrada: a
 * leitura tolerante (`effectiveBaixas`) e a conversão de write legado feita
 * pelo trigger. O id determinístico `legacy-<labelId>` é o que torna a
 * migração e o retry idempotentes.
 *
 * `EXPIRED` sai sempre com `byUid = SYSTEM`: na leitura de um doc v1 não há
 * como saber se `statusChangedByUid` é de quem expirou ou de quem imprimiu
 * (o "nome fantasma" da #148). A conversão de write legado do trigger
 * (`convertLegacyWrite` em `apps/functions/src/lib/labelLedger.ts`) SOBRESCREVE
 * esse `byUid` quando o write em mãos prova que um humano gravou o uid agora —
 * o "Expirar agora" de OWNER/MANAGER (#795, DAT-5, 2026-10-03). Por isso esta
 * função continua igual aos espelhos `LabelQuantity.kt`/`LabelQuantity.swift`:
 * a exceção mora no trigger, onde o `before` existe.
 *
 * Etiqueta legada com `quantity == 0` fica sem entrada: `LabelBaixaSchema`
 * exige `quantity` positiva, e "baixa de zero" não é um fato. O `status`
 * continua dizendo o desfecho — é a exceção documentada de "fechada sem ledger".
 */
export function syntheticLegacyLedger(
  label: LabelQuantitySource,
  labelId: string,
  options: { readonly quantity?: number; readonly migrationRunId?: string } = {},
): LabelBaixa[] {
  const status = label.status ?? '';
  const kind: LabelOutcomeKind | null =
    status === LabelStatus.CONSUMED
      ? 'CONSUMED'
      : status === LabelStatus.DISCARDED
        ? 'DISCARDED'
        : status === LabelStatus.EXPIRED
          ? 'EXPIRED'
          : null;
  if (kind === null) return [];

  const quantity = round3(options.quantity ?? num(label.quantity));
  if (quantity <= 0) return [];

  const atMillis =
    num(label.statusChangedAtMillis) ||
    num(label.printedAtMillis) ||
    num(label.createdAtMillis);
  const isExpired = kind === 'EXPIRED';
  const byUid = isExpired
    ? SYSTEM_ACTOR_UID
    : (label.statusChangedByUid ?? label.printedByUid ?? SYSTEM_ACTOR_UID);
  const via = isExpired
    ? null
    : ((label.statusChangedVia as StatusChangedVia | null) ?? StatusChangedVia.INLINE);

  return [
    {
      kind,
      id: `legacy-${labelId}`,
      quantity,
      atMillis,
      byUid: byUid as LabelBaixaMovement['byUid'],
      byName: label.statusChangedByName ?? '',
      via,
      platform: 'SERVER',
      ...(options.migrationRunId === undefined ? {} : { migrationRunId: options.migrationRunId }),
    },
  ];
}

/**
 * O ledger da etiqueta, tolerante a doc v1: se o doc ainda não foi migrado
 * (F2/#331), devolve a entrada sintética da tabela da §2.5. Depois da migração
 * todo doc é v2 e este fallback nunca dispara.
 */
export function effectiveBaixas(
  label: LabelQuantitySource,
  labelId: string,
): readonly LabelBaixa[] {
  const baixas = ledgerEntries(label.baixas);
  if (baixas.length > 0 || isLabelSchemaV2(label)) return baixas;
  return syntheticLegacyLedger(label, labelId);
}

/**
 * Doc v2 cujo saldo ainda **não foi materializado**: vigente, sem movimento no
 * ledger e com os quatro acumuladores zerados, apesar de ter sido impresso com
 * quantidade.
 *
 * Acontece em duas janelas reais: o `create` feito por um cliente que não
 * escreve os campos do contrato (o trigger normaliza depois) e o intervalo
 * entre o write e a reconciliação. Ler o campo cru nesse estado diz
 * "0 disponível" numa etiqueta impressa inteira — e, pior, **impede qualquer
 * baixa**, porque o teto do campo de quantidade é o vigente. Foi assim que o
 * bug apareceu no device durante a F3 do Android (#325).
 */
function isPendingProjection(label: LabelQuantitySource): boolean {
  const status = label.status ?? '';
  if (status !== LabelStatus.DRAFT && status !== LabelStatus.PRINTED) return false;
  return (
    num(label.quantity) > QUANTITY_EPSILON &&
    num(label.remainingQuantity) <= QUANTITY_EPSILON &&
    num(label.consumedQuantity) <= QUANTITY_EPSILON &&
    num(label.discardedQuantity) <= QUANTITY_EPSILON &&
    num(label.expiredQuantity) <= QUANTITY_EPSILON
  );
}

function legacyProjection(label: LabelQuantitySource): LabelProjection {
  const quantity = round3(Math.max(0, num(label.quantity)));
  const status = label.status ?? '';
  const zero = {
    consumedQuantity: 0,
    discardedQuantity: 0,
    expiredQuantity: 0,
    overrunQuantity: 0,
  };
  switch (status) {
    case LabelStatus.CONSUMED:
      return { ...zero, consumedQuantity: quantity, remainingQuantity: 0 };
    case LabelStatus.DISCARDED:
      return { ...zero, discardedQuantity: quantity, remainingQuantity: 0 };
    case LabelStatus.EXPIRED:
      return { ...zero, expiredQuantity: quantity, remainingQuantity: 0 };
    // `MIXED` não existe em doc v1 (ninguém o escreveu antes da F3); se
    // aparecer, é fechada e sem composição conhecida — nunca vigente.
    case LabelStatus.MIXED:
      return { ...zero, remainingQuantity: 0 };
    default:
      return { ...zero, remainingQuantity: quantity };
  }
}

/**
 * A projeção da etiqueta, venha ela de doc v2 (campos materializados) ou v1
 * (deduzida da tabela da §2.5). Um doc com ledger não vazio sempre é
 * reprojetado do ledger — ele é a verdade, mesmo que os caches divirjam.
 */
export function effectiveProjection(label: LabelQuantitySource): LabelProjection {
  const baixas = ledgerEntries(label.baixas);
  if (baixas.length > 0) {
    return computeLabelProjection(num(label.quantity), effectiveLedger(baixas));
  }
  // Vigente sem ledger e com saldo zerado é doc que o trigger ainda não
  // normalizou; a tabela da §2.5 dá a resposta certa (saldo cheio) até ele
  // responder. Ver `isPendingProjection`.
  if (isLabelSchemaV2(label) && isPendingProjection(label)) {
    return legacyProjection(label);
  }
  if (isLabelSchemaV2(label)) {
    return {
      consumedQuantity: round3(num(label.consumedQuantity)),
      discardedQuantity: round3(num(label.discardedQuantity)),
      expiredQuantity: round3(num(label.expiredQuantity)),
      remainingQuantity: round3(num(label.remainingQuantity)),
      overrunQuantity: round3(num(label.overrunQuantity)),
    };
  }
  return legacyProjection(label);
}

/**
 * SALDO VIGENTE da etiqueta. É o número que o Estoque, a lista de vigentes, o
 * agrupamento, o scanner, o detalhe, os alertas e o Monitor mostram — e o que
 * o `sum()` agrega quando 100% da base for v2 (resolve #208).
 */
export function effectiveRemaining(label: LabelQuantitySource): number {
  return effectiveProjection(label).remainingQuantity;
}

export function effectiveConsumed(label: LabelQuantitySource): number {
  return effectiveProjection(label).consumedQuantity;
}

export function effectiveDiscarded(label: LabelQuantitySource): number {
  return effectiveProjection(label).discardedQuantity;
}

export function effectiveExpired(label: LabelQuantitySource): number {
  return effectiveProjection(label).expiredQuantity;
}

/**
 * `true` quando a UI pode oferecer **Desfazer** naquela entrada.
 *
 * Só desfecho ainda sem estorno (estorno não se estorna, e uma entrada é
 * estornada no máximo uma vez). A entrada `EXPIRED` só volta enquanto a
 * validade estiver no futuro — é o "expirar manual antecipado" do #230; se
 * venceu de verdade, desfazer devolveria alimento fora do prazo ao controle de
 * validade e o cron re-expiraria em ≤30 min. No teto do ledger não cabe a
 * linha de estorno, então o botão some.
 */
export function canUndoEntry(
  entry: LabelBaixa,
  ledger: EffectiveLedger,
  options: { readonly expiresAtMillis?: number | null; readonly nowMillis: number },
): boolean {
  if (entry.kind === 'UNDO') return false;
  if (!ledger.outcomes.some((it) => it.id === entry.id)) return false;
  if (ledger.size >= LABEL_BAIXAS_MAX) return false;
  if (entry.kind === 'EXPIRED') {
    return num(options.expiresAtMillis) > options.nowMillis;
  }
  return true;
}
